import { useState, type KeyboardEvent } from 'react';
import { Pencil, Trash2 } from 'lucide-react';
import { ModaleConfirmation } from '../contrats/ModaleConfirmation';
import { MessageErreur } from '../ui/Primitives';
import { useToast } from '../ui/useToast';
import { ErreurApi } from '../../lib/api';
import { tempsLisible } from '../../lib/temps-video';
import { useModifierNote, useSupprimerNote, type NoteLecon } from '../../lib/useLecteur';

/** Même limite que l'API (`domain/notes-lecon.ts`). */
export const TEXTE_NOTE_MAX = 2000;

const CLASSE_ICONE =
  'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-codialis-gray transition hover:bg-yellow-100 hover:text-codialis-navy [@media(pointer:coarse)]:h-10 [@media(pointer:coarse)]:w-10';

export const CLASSE_BOUTON_NOTE =
  'inline-flex min-h-[36px] items-center gap-1.5 rounded-lg px-3 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-50 [@media(pointer:coarse)]:min-h-[44px]';

/** Compteur affiché quand la note approche de la limite. */
export function ResteCaracteres({ texte }: { texte: string }): JSX.Element | null {
  if (texte.length < TEXTE_NOTE_MAX - 200) return null;
  return (
    <span className="text-[11px] text-codialis-gray">
      {texte.length} / {TEXTE_NOTE_MAX} caractères
    </span>
  );
}

/**
 * Une note personnelle (TF-446), sur le modèle de la maquette : son moment
 * dans la vidéo — qui y renvoie quand la vidéo est là —, son texte, et les
 * gestes de son auteur : modifier le texte, supprimer (après confirmation).
 */
export function CarteNote({
  note,
  contexte,
  allerA,
}: {
  note: NoteLecon;
  /** « Module 1 » : lu après le moment. */
  contexte?: string;
  /** Envoie la vidéo au moment de la note ; `null` quand elle ne s'ouvre pas. */
  allerA: (() => void) | null;
}): JSX.Element {
  const toast = useToast();
  const modifier = useModifierNote();
  const supprimer = useSupprimerNote();
  const [brouillon, setBrouillon] = useState<string | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState(false);
  const moment = tempsLisible(note.momentSecondes);

  const enregistrer = async () => {
    if (brouillon === null || !brouillon.trim()) return;
    setErreur(null);
    try {
      await modifier.mutateAsync({ id: note.id, texte: brouillon });
      setBrouillon(null);
      toast('Note modifiée');
    } catch (cause) {
      setErreur(cause instanceof ErreurApi ? cause.message : 'La note n’a pas pu être modifiée.');
    }
  };

  const confirmerSuppression = async () => {
    setErreur(null);
    try {
      await supprimer.mutateAsync(note.id);
      toast('Note supprimée');
    } catch (cause) {
      setErreur(cause instanceof ErreurApi ? cause.message : 'La note n’a pas pu être supprimée.');
    } finally {
      setConfirmation(false);
    }
  };

  const clavier = (evenement: KeyboardEvent<HTMLTextAreaElement>) => {
    if (evenement.key === 'Enter' && (evenement.ctrlKey || evenement.metaKey)) {
      evenement.preventDefault();
      void enregistrer();
    } else if (evenement.key === 'Escape') {
      setBrouillon(null);
    }
  };

  return (
    <li className="rounded border-l-4 border-yellow-400 bg-yellow-50 p-3">
      <div className="mb-1 flex items-start gap-2">
        <div className="min-w-0 flex-1 text-xs text-codialis-gray">
          {allerA ? (
            <button
              type="button"
              onClick={allerA}
              aria-label={`Aller à ${moment} dans la vidéo`}
              className="font-semibold text-codialis-sky hover:underline [@media(pointer:coarse)]:min-h-[40px]"
            >
              À {moment}
            </button>
          ) : (
            <span>À {moment}</span>
          )}
          {contexte && ` · ${contexte}`}
          {note.videoChangee && (
            <span className="block text-amber-800">Prise sur une version précédente de la vidéo : le moment peut avoir changé.</span>
          )}
        </div>
        {brouillon === null && (
          <div className="-mr-1 -mt-1 flex shrink-0">
            <button
              type="button"
              onClick={() => {
                setErreur(null);
                setBrouillon(note.texte);
              }}
              aria-label={`Modifier la note de ${moment}`}
              title="Modifier"
              className={CLASSE_ICONE}
            >
              <Pencil className="h-4 w-4" aria-hidden />
            </button>
            <button
              type="button"
              onClick={() => setConfirmation(true)}
              aria-label={`Supprimer la note de ${moment}`}
              title="Supprimer"
              className={CLASSE_ICONE}
            >
              <Trash2 className="h-4 w-4" aria-hidden />
            </button>
          </div>
        )}
      </div>

      {brouillon === null ? (
        <p className="whitespace-pre-line break-words text-sm text-codialis-navy">{note.texte}</p>
      ) : (
        <div className="space-y-2">
          <textarea
            value={brouillon}
            rows={3}
            maxLength={TEXTE_NOTE_MAX}
            autoFocus
            onChange={(evenement) => setBrouillon(evenement.target.value)}
            onKeyDown={clavier}
            aria-label={`Texte de la note de ${moment}`}
            className="w-full resize-y rounded-xl border border-gray-200 bg-white p-3 text-sm text-codialis-navy focus:outline-none focus:ring-2 focus:ring-codialis-mint"
          />
          <div className="flex flex-wrap items-center justify-end gap-2">
            <span className="min-w-0 flex-1">
              <ResteCaracteres texte={brouillon} />
            </span>
            <button
              type="button"
              onClick={() => setBrouillon(null)}
              className={`${CLASSE_BOUTON_NOTE} border border-gray-200 bg-white text-codialis-navy hover:bg-gray-50`}
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={() => void enregistrer()}
              disabled={!brouillon.trim() || brouillon.trim() === note.texte || modifier.isPending}
              className={`${CLASSE_BOUTON_NOTE} bg-codialis-navy text-white hover:bg-codialis-deep`}
            >
              {modifier.isPending ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          </div>
        </div>
      )}
      {erreur && (
        <div className="mt-2">
          <MessageErreur message={erreur} />
        </div>
      )}

      <ModaleConfirmation
        ouverte={confirmation}
        titre="Supprimer la note"
        message={`La note de ${moment} sera supprimée définitivement.`}
        libelleConfirmer="Supprimer"
        enCours={supprimer.isPending}
        onConfirmer={() => void confirmerSuppression()}
        onAnnuler={() => setConfirmation(false)}
      />
    </li>
  );
}
